import { useCallback, useEffect, useState } from "react";
import { Save, Trash2 } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { OrderSettingsPanel } from "@/components/orders/OrderSettingsPanel";
import { api } from "@/lib/api-client";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { CertificateProfile } from "@/lib/domain";

const COUNTRIES = ["IT", "ES", "FR", "DE", "AE", "GB", "US", "SA"];
const LOCALES = ["it", "en", "es"] as const;

type Draft = Partial<CertificateProfile> & { country: string; entityName: string };

const EMPTY: Draft = {
  country: "IT",
  entityName: "",
  entityAddress: "",
  vatNumber: "",
  registrationNo: "",
  guarantorName: "",
  insurancePolicyRef: "",
  insuranceInsurer: "",
  defaultLocale: "it",
  active: true,
};

const TEXT = {
  it: {
    kicker: "Impostazioni",
    title: "Enti emittenti per paese",
    intro:
      "Il certificato riporta l'ente del paese dell'opera. I valori vengono copiati sul certificato al momento dell'emissione: modifiche successive non riscrivono i certificati già emessi (per aggiornarli si emette una nuova revisione).",
    add: "Aggiungi paese",
    entity: "Ragione sociale",
    address: "Indirizzo",
    vat: "Partita IVA / VAT",
    registration: "Registro imprese",
    guarantor: "Garante",
    insurer: "Assicuratore",
    policy: "Polizza assicurativa",
    locale: "Lingua del certificato",
    active: "Attivo",
    save: "Salva",
    saved: "Salvato",
    remove: "Elimina",
    confirm: "Eliminare questo profilo?",
    newCountry: "Nuovo paese",
  },
  en: {
    kicker: "Settings",
    title: "Issuing entities by country",
    intro:
      "The certificate prints the entity for the project's country. Values are copied onto the certificate at issue time, so later edits never rewrite issued certificates — re-issue to apply a change.",
    add: "Add country",
    entity: "Legal name",
    address: "Address",
    vat: "VAT number",
    registration: "Company register",
    guarantor: "Guarantor",
    insurer: "Insurer",
    policy: "Insurance policy",
    locale: "Certificate language",
    active: "Active",
    save: "Save",
    saved: "Saved",
    remove: "Delete",
    confirm: "Delete this profile?",
    newCountry: "New country",
  },
  es: {
    kicker: "Configuración",
    title: "Entidades emisoras por país",
    intro:
      "El certificado muestra la entidad del país de la obra. Los valores se copian al certificado en el momento de la emisión, por lo que las ediciones posteriores no reescriben los certificados ya emitidos: emite una nueva revisión para aplicarlos.",
    add: "Añadir país",
    entity: "Razón social",
    address: "Dirección",
    vat: "NIF / VAT",
    registration: "Registro mercantil",
    guarantor: "Garante",
    insurer: "Aseguradora",
    policy: "Póliza de seguro",
    locale: "Idioma del certificado",
    active: "Activo",
    save: "Guardar",
    saved: "Guardado",
    remove: "Eliminar",
    confirm: "¿Eliminar este perfil?",
    newCountry: "Nuevo país",
  },
} as const;

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <input
        className="field"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export default function SettingsPage() {
  const { locale } = useLocale();
  const tx = TEXT[locale];
  const [profiles, setProfiles] = useState<CertificateProfile[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [adding, setAdding] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"profiles" | "orders">("profiles");

  const load = useCallback(async () => {
    try {
      const rows = await api.certificateProfiles();
      setProfiles(rows);
      setDrafts(Object.fromEntries(rows.map((r) => [r.id, { ...r }])));
    } catch {
      setProfiles([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const set = (id: string, patch: Partial<Draft>) =>
    setDrafts((prev) => {
      const current = prev[id];
      if (!current) return prev;
      return { ...prev, [id]: { ...current, ...patch } };
    });

  async function save(draft: Draft & { id?: string }) {
    const key = draft.id ?? "new";
    setBusy(key);
    setError(null);
    try {
      if (draft.id) {
        await api.updateCertificateProfile(draft.id, draft);
        setSavedId(draft.id);
        setTimeout(() => setSavedId(null), 1500);
      } else {
        await api.createCertificateProfile(draft);
        setAdding(null);
      }
      await load();
    } catch (err) {
      const message = err instanceof Error ? err.message : "error";
      try {
        const parsed = JSON.parse(message) as { message?: string };
        setError(parsed.message ?? message);
      } catch {
        setError(message);
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <AppShell>
      <div className="mb-4 flex gap-1 rounded-full border border-white/15 p-0.5 sm:w-fit">
        {(["profiles", "orders"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "flex-1 rounded-full px-4 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition",
              tab === k ? "bg-white/15 text-paper" : "text-ice/55",
            )}
          >
            {k === "profiles" ? tx.title : "Orders"}
          </button>
        ))}
      </div>
      {tab === "orders" ? (
        <>
          <div className="brand-kicker">{tx.kicker}</div>
          <h1 className="font-serif text-2xl text-paper sm:text-3xl">Orders</h1>
          <OrderSettingsPanel />
        </>
      ) : (
        <>
      <div className="brand-kicker">{tx.kicker}</div>
      <h1 className="font-serif text-2xl text-paper sm:text-3xl">{tx.title}</h1>
      <p className="mt-2 max-w-3xl text-[0.78rem] leading-relaxed text-ice/65">
        {tx.intro}
      </p>

      {error && <p className="mt-3 text-xs text-pink">{error}</p>}

      <div className="mt-6 flex items-center justify-between gap-3">
        <h2 className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
          {profiles?.length ?? 0}
        </h2>
        <button
          type="button"
          onClick={() =>
            setAdding(
              adding
                ? null
                : { ...EMPTY, country: COUNTRIES[0] ?? "IT" },
            )
          }
          className="rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ice/70 hover:text-paper"
        >
          {adding ? "×" : "+ "}
          {tx.add}
        </button>
      </div>

      {adding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save(adding);
          }}
          className="glass-panel-strong mt-4 grid gap-3 p-5 sm:grid-cols-3"
        >
          <label className="block">
            <span className="field-label">{tx.newCountry}</span>
            <select
              className="field"
              value={adding.country}
              onChange={(e) => setAdding({ ...adding, country: e.target.value })}
            >
              {COUNTRIES.filter(
                (c) => !profiles?.some((p) => p.country === c),
              ).map((c) => (
                <option key={c} value={c} className="bg-night">
                  {c}
                </option>
              ))}
            </select>
          </label>
          <div className="sm:col-span-2">
            <Field
              label={tx.entity}
              value={adding.entityName}
              onChange={(v) => setAdding({ ...adding, entityName: v })}
            />
          </div>
          <label className="block">
            <span className="field-label">{tx.locale}</span>
            <select
              className="field"
              value={adding.defaultLocale}
              onChange={(e) =>
                setAdding({
                  ...adding,
                  defaultLocale: e.target.value as Draft["defaultLocale"],
                })
              }
            >
              {LOCALES.map((l) => (
                <option key={l} value={l} className="bg-night">
                  {l}
                </option>
              ))}
            </select>
          </label>
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={busy === "new" || !adding.entityName}
              className="rounded-full bg-magenta px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-50"
            >
              {busy === "new" ? "…" : tx.save}
            </button>
          </div>
        </form>
      )}

      <div className="mt-4 grid gap-4">
        {profiles?.length === 0 && (
          <p className="text-sm text-ice/60">—</p>
        )}

        {profiles?.map((p) => {
          const d = drafts[p.id] ?? p;
          return (
            <form
              key={p.id}
              onSubmit={(e) => {
                e.preventDefault();
                void save({ ...d, id: p.id });
              }}
              className={cn(
                "glass-panel-strong p-5",
                !p.active && "opacity-60",
              )}
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="num grid h-9 w-11 place-items-center rounded-lg border border-white/15 font-serif text-sm text-paper">
                  {p.country}
                </span>
                <span className="font-serif text-base text-paper">
                  {d.entityName || "—"}
                </span>
                <span className="rounded-full border border-white/15 px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.14em] text-ice/60">
                  {d.defaultLocale}
                </span>
                {savedId === p.id && (
                  <span className="text-[0.62rem] text-green">{tx.saved}</span>
                )}
                <span className="ml-auto flex items-center gap-3">
                  <label className="flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.12em] text-ice/60">
                    <input
                      type="checkbox"
                      className="accent-magenta"
                      checked={d.active ?? true}
                      onChange={(e) => set(p.id, { active: e.target.checked })}
                    />
                    {tx.active}
                  </label>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(tx.confirm)) return;
                      await api.deleteCertificateProfile(p.id);
                      await load();
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-rose/50 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-pink"
                  >
                    <Trash2 className="h-3 w-3" />
                    {tx.remove}
                  </button>
                </span>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Field
                    label={tx.entity}
                    value={d.entityName ?? ""}
                    onChange={(v) => set(p.id, { entityName: v })}
                  />
                </div>
                <Field
                  label={tx.address}
                  value={d.entityAddress ?? ""}
                  onChange={(v) => set(p.id, { entityAddress: v })}
                />
                <Field
                  label={tx.vat}
                  value={d.vatNumber ?? ""}
                  onChange={(v) => set(p.id, { vatNumber: v })}
                />
                <Field
                  label={tx.registration}
                  value={d.registrationNo ?? ""}
                  onChange={(v) => set(p.id, { registrationNo: v })}
                />
                <Field
                  label={tx.guarantor}
                  value={d.guarantorName ?? ""}
                  onChange={(v) => set(p.id, { guarantorName: v })}
                />
                <Field
                  label={tx.insurer}
                  value={d.insuranceInsurer ?? ""}
                  onChange={(v) => set(p.id, { insuranceInsurer: v })}
                />
                <Field
                  label={tx.policy}
                  value={d.insurancePolicyRef ?? ""}
                  onChange={(v) => set(p.id, { insurancePolicyRef: v })}
                />
                <label className="block">
                  <span className="field-label">{tx.locale}</span>
                  <select
                    className="field"
                    value={d.defaultLocale}
                    onChange={(e) =>
                      set(p.id, {
                        defaultLocale: e.target.value as Draft["defaultLocale"],
                      })
                    }
                  >
                    {LOCALES.map((l) => (
                      <option key={l} value={l} className="bg-night">
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <button
                type="submit"
                disabled={busy === p.id}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-magenta px-4 py-2 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-50"
              >
                <Save className="h-3 w-3" />
                {busy === p.id ? "…" : tx.save}
              </button>
            </form>
          );
        })}
      </div>
        </>
      )}
    </AppShell>
  );
}
